import React, { useState, useEffect } from 'react';

const MetaBudgetIncreaseHistory = ({ recordId, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHistory();
  }, [recordId]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      
      // Determinar la URL de la API basada en el entorno
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_API_URL || 'https://dashboard.doctorweb.top/api'
        : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

      const response = await fetch(`${API_URL}/meta-budget-increase/${recordId}/history`);
      
      if (response.ok) {
        const data = await response.json();
        setHistory(data);
      } else {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error al cargar historial:', error);
      alert('Error al cargar el historial del registro');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionIcon = (actionType) => {
    const icons = {
      'created': '➕',
      'updated': '✏️',
      'status_changed': '🔄',
      'comment_added': '💬',
      'completed': '✅',
      'cancelled': '❌'
    };
    return icons[actionType] || '📝';
  };

  const getActionLabel = (actionType) => {
    const labels = {
      'created': 'Registro creado',
      'updated': 'Registro actualizado',
      'status_changed': 'Estado cambiado',
      'comment_added': 'Comentario agregado',
      'completed': 'Marcado como completado',
      'cancelled': 'Cancelado'
    };
    return labels[actionType] || actionType;
  };

  const getActionColor = (actionType) => {
    const colors = {
      'created': 'text-green-600 bg-green-50',
      'updated': 'text-blue-600 bg-blue-50',
      'status_changed': 'text-purple-600 bg-purple-50',
      'comment_added': 'text-gray-600 bg-gray-50',
      'completed': 'text-green-600 bg-green-50',
      'cancelled': 'text-red-600 bg-red-50'
    };
    return colors[actionType] || 'text-gray-600 bg-gray-50';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold flex items-center">
                <span className="mr-3">📜</span>
                Historial de Aumento de Presupuesto
              </h2>
              <p className="text-purple-100 mt-1">
                Registro completo de cambios y acciones realizadas
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl font-bold transition-colors"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
              <span className="ml-3 text-lg text-gray-600">Cargando historial...</span>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📝</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Sin historial disponible
              </h3>
              <p className="text-gray-500">
                No hay registros de cambios para este aumento de presupuesto.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Timeline */}
              <div className="relative">
                {history.map((entry, index) => (
                  <div key={entry.id} className="relative flex items-start space-x-4 pb-8">
                    {/* Timeline line */}
                    {index < history.length - 1 && (
                      <div className="absolute left-6 top-12 w-0.5 h-full bg-gray-200"></div>
                    )}
                    
                    {/* Icon */}
                    <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold ${getActionColor(entry.action_type)}`}>
                      {getActionIcon(entry.action_type)}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                        {/* Header */}
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900">
                              {getActionLabel(entry.action_type)}
                            </h4>
                            <p className="text-xs text-gray-500">
                              {formatDate(entry.created_at)}
                            </p>
                          </div>
                          <div className="text-xs text-gray-400">
                            por {entry.user_name || 'Sistema'}
                          </div>
                        </div>
                        
                        {/* Details */}
                        {entry.details && (
                          <div className="mt-3">
                            <div className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">
                              {entry.details}
                            </div>
                          </div>
                        )}
                        
                        {/* Previous and New Values */}
                        {entry.previous_value && entry.new_value && (
                          <div className="mt-3 grid grid-cols-2 gap-4">
                            <div>
                              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                Valor Anterior
                              </label>
                              <div className="mt-1 text-sm text-gray-900 bg-red-50 rounded px-2 py-1">
                                {entry.previous_value}
                              </div>
                            </div>
                            <div>
                              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                Nuevo Valor
                              </label>
                              <div className="mt-1 text-sm text-gray-900 bg-green-50 rounded px-2 py-1">
                                {entry.new_value}
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Field Changed */}
                        {entry.field_changed && (
                          <div className="mt-2">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Campo: {entry.field_changed}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t flex justify-between items-center">
          <div className="text-sm text-gray-500">
            {history.length > 0 && `${history.length} entrada${history.length !== 1 ? 's' : ''} en el historial`}
          </div>
          <button
            onClick={onClose}
            className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default MetaBudgetIncreaseHistory;
